import { useState } from 'react'
import { AlertCircle, CheckCircle, ChevronDown, Send } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { useErrorReporter } from '@/contexts/ErrorReporterContext'
import { collectErrorContext, sanitizeForDisplay } from '@/utils/ErrorContext.utils'
import { useAuth } from '@/hooks/UseAuth'
import type { ErrorSeverity } from '@/types/Error.types'
import './error-report-dialog.css'

/**
 * Error Report Dialog
 *
 * A dialog that appears when an error is captured, allowing users to:
 * - See a summary of what went wrong
 * - Add a description of what they were trying to do
 * - Preview the technical details that will be sent
 * - Submit the report to the development team
 */
export function ErrorReportDialog() {
	const { user } = useAuth()
	const { state, closeDialog, submitReport, recentActions } = useErrorReporter()
	const [description, setDescription] = useState('')
	const [severity, setSeverity] = useState<ErrorSeverity>('medium')
	const [showDetails, setShowDetails] = useState(false)

	const isOpen = state.status !== 'idle'
	const isSubmitting = state.status === 'submitting'
	const isSuccess = state.status === 'success'
	const isError = state.status === 'error'

	// Collect and sanitize context for display
	const context =
		state.error && state.status === 'preview'
			? sanitizeForDisplay(
				collectErrorContext(
					state.error,
					state.componentStack,
					recentActions,
					user ? { id: user.id, name: user.name } : null,
				),
			)
			: null

	function handleSubmit() {
		submitReport(description, severity)
	}

	function handleClose() {
		if (!isSubmitting) {
			setDescription('')
			setSeverity('medium')
			setShowDetails(false)
			closeDialog()
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="error-report-dialog">
				{/* Success overlay */}
				{isSuccess && (
					<div className="error-report-success">
						<CheckCircle className="error-report-success-icon" />
						<span className="error-report-success-text">Report sent successfully</span>
						<span className="error-report-success-subtext">Thank you for helping us improve!</span>
					</div>
				)}

				{/* Main dialog content */}
				{!isSuccess && (
					<>
						<DialogHeader>
							<DialogTitle>Report an Error</DialogTitle>
							<DialogDescription>
								Help us improve by sharing details about what went wrong.
							</DialogDescription>
						</DialogHeader>

						<div className="error-report-body">
							{/* Error summary */}
							<div className="error-summary">
								<AlertCircle className="error-summary-icon" />
								<div className="error-summary-content">
									<span className="error-summary-name">{state.error?.name ?? 'Error'}</span>
									<span className="error-summary-message">{state.error?.message}</span>
								</div>
							</div>

							{/* User description */}
							<div className="error-report-field">
								<label htmlFor="error-description" className="error-report-label">
									What were you trying to do? <span className="optional">(optional)</span>
								</label>
								<textarea
									id="error-description"
									className="error-report-textarea"
									placeholder="I was trying to..."
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={3}
									disabled={isSubmitting}
								/>
							</div>

							{/* Severity selector */}
							<div className="error-report-field">
								<label className="error-report-label">How severe is this issue?</label>
								<div className="error-report-severity">
									{(['low', 'medium', 'high', 'critical'] as const).map((level) => (
										<button
											key={level}
											type="button"
											className="error-report-severity-option"
											data-severity={level}
											data-selected={severity === level}
											onClick={() => setSeverity(level)}
											disabled={isSubmitting}
										>
											{level.charAt(0).toUpperCase() + level.slice(1)}
										</button>
									))}
								</div>
							</div>

							{/* Collapsible details */}
							<button
								type="button"
								className="error-report-details-toggle"
								onClick={() => setShowDetails(!showDetails)}
								disabled={isSubmitting}
							>
								<ChevronDown
									className="error-report-details-icon"
									data-expanded={showDetails}
								/>
								<span>View report details</span>
							</button>

							{showDetails && context && (
								<div className="error-report-details">
									<pre className="error-report-details-content">
										{JSON.stringify(context, null, 2)}
									</pre>
								</div>
							)}

							{/* Error message for failed submission */}
							{isError && (
								<div className="error-report-error">
									{state.submissionError ?? 'Failed to submit report. Please try again.'}
								</div>
							)}
						</div>

						{/* Footer actions */}
						<div className="error-report-footer">
							<Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
								Cancel
							</Button>
							<Button
								variant="default"
								onClick={handleSubmit}
								disabled={isSubmitting}
								className="error-report-submit"
							>
								{isSubmitting ? (
									<span className="error-report-loading">Sending...</span>
								) : (
									<>
										<Send className="error-report-submit-icon" />
										Send to PlaySmith
									</>
								)}
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}
